import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { LuFolderPlus, LuTags, LuClipboardCheck, LuSplit } from "react-icons/lu";
import { API, colors, PART_LABELS, PART_FLAGS, TYPE_ORDER } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";
import { FlatAssetRegister } from "./FlatAssetRegister";
import { UnitsRegister } from "./UnitsRegister";
import { CreateInspectionModal } from "./CreateInspectionModal";
import { BatchCreateInspectionModal } from "./BatchCreateInspectionModal";
import { InspectionSheet } from "../inspections/InspectionSheet";

const UNSECTIONED_LABEL_FIELD = {
  whole: "unsectioned_label",
  unit_group: "unit_group_unsectioned_label",
};

const UNSECTIONED_HIDDEN_FIELD = {
  whole: "hide_unsectioned_whole",
  unit_group: "hide_unsectioned_units",
};

function IconBtn({ onClick, title, icon, label, hoverBg, hoverColor }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onClick} title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? hoverBg : "#f1f5f9",
        border: "none", borderRadius: "6px", padding: "8px 10px",
        cursor: "pointer", color: hovered ? hoverColor : "#64748b",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
        fontSize: "12px", fontWeight: "600",
        transition: "background 0.15s, color 0.15s",
      }}>
      {icon}
      {label}
    </button>
  );
}

export function AssetRegisterTabs({ siteId, site, onSiteChange }) {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editingDraft, setEditingDraft] = useState("");
  const [showAddCompartment, setShowAddCompartment] = useState(false);
  const [newCompartmentType, setNewCompartmentType] = useState("common");
  const [newCompartmentName, setNewCompartmentName] = useState("");
  const [addingCompartment, setAddingCompartment] = useState(false);
  const [addCompartmentError, setAddCompartmentError] = useState("");
  const [showAddTenantCategory, setShowAddTenantCategory] = useState(false);
  const [newTenantCategoryName, setNewTenantCategoryName] = useState("");
  const [addingTenantCategory, setAddingTenantCategory] = useState(false);
  const [addTenantCategoryError, setAddTenantCategoryError] = useState("");
  const [showCreateInspection, setShowCreateInspection] = useState(false);
  const [createdInspectionId, setCreatedInspectionId] = useState(null);
  const [showBatchInspection, setShowBatchInspection] = useState(false);
  const editInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const tenantCategoryInputRef = useRef(null);

  const loadAreas = () => {
    axios.get(`${API}/site-areas/?site=${siteId}`)
      .then((res) => { setAreas(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadAreas(); }, [siteId]);

  useEffect(() => {
    if (editingKey) editInputRef.current?.focus();
  }, [editingKey]);

  useEffect(() => {
    if (showAddCompartment) nameInputRef.current?.focus();
  }, [showAddCompartment]);

  useEffect(() => {
    if (showAddTenantCategory) tenantCategoryInputRef.current?.focus();
  }, [showAddTenantCategory]);

  const hasRealGeneralSections = areas.some((a) => ["common", "warehouse", "building"].includes(a.area_type));
  const hasRealUnitSections = areas.some((a) => a.area_type === "unit_group");
  const wholeRetired = hasRealGeneralSections && !site.has_unassigned_whole_assets;
  const unitsRetired = hasRealUnitSections && !site.has_unassigned_units;

  const tabs = [];
  if (site.has_whole_building_register && !site.hide_unsectioned_whole && !wholeRetired) {
    tabs.push({ key: "pinned:whole", label: site.unsectioned_label || "Unsectioned", kind: "pinned", areaType: "whole" });
  }
  if (site.has_units && !site.hide_unsectioned_units && !unitsRetired) {
    tabs.push({ key: "pinned:unit_group", label: site.unit_group_unsectioned_label || "Unsectioned", kind: "pinned", areaType: "unit_group" });
  }
  areas.forEach((a) => {
    if (site[PART_FLAGS[a.area_type]]) {
      tabs.push({ key: String(a.id), label: a.name, kind: "area", areaType: a.area_type, area: a });
    }
  });
  // Disambiguate tabs that happen to share the same name (e.g. leftover
  // duplicates created before section names were required to be unique
  // per site) so they're never visually indistinguishable in the bar.
  const nameCounts = {};
  tabs.forEach((t) => {
    const key = t.label.trim().toLowerCase();
    nameCounts[key] = (nameCounts[key] || 0) + 1;
  });
  tabs.forEach((t) => {
    const key = t.label.trim().toLowerCase();
    if (nameCounts[key] > 1) {
      t.label = `${t.label} (${PART_LABELS[t.areaType] || t.areaType})`;
    }
  });
  const tabsKey = tabs.map((t) => t.key).join(",");

  useEffect(() => {
    if (!tabs.find((t) => t.key === activeKey)) {
      setActiveKey(tabs[0]?.key ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsKey]);

  const toggleUnsectionedHidden = async (areaType, next) => {
    try {
      await axios.patch(`${API}/sites/${siteId}/`, { [UNSECTIONED_HIDDEN_FIELD[areaType]]: next });
      onSiteChange && onSiteChange();
    } catch {}
  };

  const toggleMoveBar = async (next) => {
    try {
      await axios.patch(`${API}/sites/${siteId}/`, { hide_move_to_section: next });
      onSiteChange && onSiteChange();
    } catch {}
  };

  const handleDeleteUnsectionedUnits = async () => {
    const label = site.unit_group_unsectioned_label || "Unsectioned";
    if (!window.confirm(`Remove "${label}" and everything in it? This cannot be undone.`)) return;
    const res = await axios.get(`${API}/site-units/?site=${siteId}`);
    const ungrouped = res.data.filter((u) => !u.area);
    await Promise.all(ungrouped.map((u) => axios.delete(`${API}/site-units/${u.id}/`)));
    await toggleUnsectionedHidden("unit_group", true);
  };

  const handleDeleteArea = async (area) => {
    if (!window.confirm(`Remove "${area.name}" and all its assets? This cannot be undone.`)) return;
    await axios.delete(`${API}/site-areas/${area.id}/`);
    loadAreas();
  };

  const startEditing = (tab) => {
    setEditingKey(tab.key);
    setEditingDraft(
      tab.kind === "pinned"
        ? (tab.areaType === "whole" ? site.unsectioned_label : site.unit_group_unsectioned_label) || ""
        : tab.area.name
    );
  };

  const saveEditing = async (tab) => {
    const trimmed = editingDraft.trim();
    setEditingKey(null);
    if (tab.kind === "pinned") {
      const current = (tab.areaType === "whole" ? site.unsectioned_label : site.unit_group_unsectioned_label) || "";
      if (trimmed === current) return;
      try {
        await axios.patch(`${API}/sites/${siteId}/`, { [UNSECTIONED_LABEL_FIELD[tab.areaType]]: trimmed });
        onSiteChange && onSiteChange();
      } catch {}
    } else {
      if (!trimmed || trimmed === tab.area.name) return;
      try {
        await axios.patch(`${API}/site-areas/${tab.area.id}/`, { name: trimmed });
        loadAreas();
      } catch (err) {
        window.alert(err.response?.data?.name?.[0] || "Rename failed.");
      }
    }
  };

  const handleAddCompartment = async () => {
    const type = newCompartmentType;
    const isPinnedType = type === "whole" || type === "unit_group";
    const trimmedName = newCompartmentName.trim();
    if (!isPinnedType && !trimmedName) {
      setAddCompartmentError("Name required.");
      return;
    }
    setAddingCompartment(true);
    setAddCompartmentError("");
    try {
      if (!site[PART_FLAGS[type]]) {
        await axios.patch(`${API}/sites/${siteId}/`, { [PART_FLAGS[type]]: true });
      }
      let newAreaId = null;
      if (trimmedName) {
        const res = await axios.post(`${API}/site-areas/`, { site: siteId, area_type: type, name: trimmedName });
        newAreaId = res.data.id;
      }
      await loadAreas();
      if (onSiteChange) await onSiteChange();
      setActiveKey(newAreaId ? String(newAreaId) : `pinned:${type}`);
      setNewCompartmentName("");
      setShowAddCompartment(false);
    } catch (err) {
      setAddCompartmentError(err.response?.data?.name?.[0] || "Save failed.");
    }
    setAddingCompartment(false);
  };

  const handleAddTenantCategory = async () => {
    const trimmedName = newTenantCategoryName.trim();
    if (!trimmedName) {
      setAddTenantCategoryError("Name required.");
      return;
    }
    setAddingTenantCategory(true);
    setAddTenantCategoryError("");
    try {
      if (!site.has_units) {
        await axios.patch(`${API}/sites/${siteId}/`, { has_units: true });
      }
      const res = await axios.post(`${API}/site-areas/`, {
        site: siteId, area_type: "unit_group", name: trimmedName,
      });
      await loadAreas();
      if (onSiteChange) await onSiteChange();
      setActiveKey(String(res.data.id));
      setNewTenantCategoryName("");
      setShowAddTenantCategory(false);
    } catch (err) {
      setAddTenantCategoryError(err.response?.data?.name?.[0] || "Save failed.");
    }
    setAddingTenantCategory(false);
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;

  if (createdInspectionId) {
    return (
      <InspectionSheet
        inspectionId={createdInspectionId}
        onBack={() => setCreatedInspectionId(null)}
      />
    );
  }

  const activeTab = tabs.find((t) => t.key === activeKey);

  const chipStyle = (isActive) => ({
    position: "relative",
    width: "160px", flex: "0 0 auto", boxSizing: "border-box",
    display: "flex", alignItems: "center", justifyContent: "center",
    textAlign: "center",
    padding: "10px 20px 10px 10px",
    cursor: "pointer",
    fontSize: "13px", lineHeight: "1.3",
    fontWeight: isActive ? "600" : "400",
    color: isActive ? colors.activeBlue : "#666",
    borderBottom: isActive ? `3px solid ${colors.activeBlue}` : "3px solid transparent",
    transition: "all 0.15s",
  });

  let content = null;
  if (activeTab) {
    if (activeTab.kind === "pinned" && activeTab.areaType === "whole") {
      content = (
        <FlatAssetRegister
          key={activeTab.key}
          siteId={siteId}
          site={site}
          onSiteChange={onSiteChange}
          scopeType="site"
          scopeId={siteId}
          moveTargets={areas.filter((a) => a.area_type !== "unit_group")}
          onMoved={loadAreas}
          moveBarHidden={site.hide_move_to_section}
          onToggleMoveBar={toggleMoveBar}
        />
      );
    } else if (activeTab.kind === "pinned" && activeTab.areaType === "unit_group") {
      content = (
        <UnitsRegister
          key={activeTab.key}
          siteId={siteId}
          scopeType="site"
          scopeId={siteId}
        />
      );
    } else if (activeTab.areaType === "unit_group") {
      content = (
        <UnitsRegister
          key={activeTab.key}
          siteId={siteId}
          scopeType="area"
          scopeId={activeTab.area.id}
        />
      );
    } else {
      content = (
        <FlatAssetRegister
          key={activeTab.key}
          siteId={siteId}
          scopeType="area"
          scopeId={activeTab.area.id}
        />
      );
    }
  }

  return (
    <div>
      <div style={{
        backgroundColor: "#fff", borderBottom: `1px solid ${colors.border}`,
        display: "flex", alignItems: "center", paddingLeft: "8px",
        flexWrap: "wrap", gap: "0", marginBottom: "8px",
      }}>
        {tabs.length === 0 && (
          <div style={{ padding: "10px 16px", fontSize: "13px", color: "#94a3b8" }}>
            No asset register compartments yet.
          </div>
        )}

        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;
          const isEditing = editingKey === tab.key;
          const showDeleteX =
            hoveredKey === tab.key && !isEditing && (
              tab.kind === "area"
                ? true
                : tab.areaType === "unit_group"
                  ? true
                  : !site.has_unassigned_whole_assets
            );
          return (
            <div key={tab.key}
              title={tab.kind === "area" ? tab.area.name : undefined}
              onMouseEnter={() => setHoveredKey(tab.key)}
              onMouseLeave={() => setHoveredKey(null)}
              onClick={() => { if (!isEditing) setActiveKey(tab.key); }}
              style={{ ...chipStyle(isActive), cursor: isEditing ? "text" : "pointer" }}>
              {isEditing ? (
                <input
                  ref={editInputRef}
                  value={editingDraft}
                  onChange={(e) => setEditingDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEditing(tab);
                    if (e.key === "Escape") setEditingKey(null);
                  }}
                  onBlur={() => saveEditing(tab)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontSize: "13px", fontWeight: "600", border: "none",
                    borderBottom: "1px solid #94a3b8", outline: "none",
                    background: "transparent", color: colors.activeBlue,
                    width: "100%", maxWidth: "140px", padding: "0", textAlign: "center",
                  }}
                />
              ) : (
                <span
                  onDoubleClick={(e) => { e.stopPropagation(); startEditing(tab); }}
                  title="Double-click to rename"
                  style={{
                    display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                    overflow: "hidden", wordBreak: "break-word",
                  }}>
                  {tab.label}
                </span>
              )}

              {showDeleteX && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (tab.kind === "area") handleDeleteArea(tab.area);
                    else if (tab.areaType === "unit_group") handleDeleteUnsectionedUnits();
                    else toggleUnsectionedHidden("whole", true);
                  }}
                  title={
                    tab.kind === "area" ? "Remove this compartment"
                      : tab.areaType === "unit_group" ? "Delete this tab and everything in it"
                      : "Hide this tab (it's empty)"
                  }
                  style={{
                    position: "absolute", top: "4px", right: "4px",
                    background: "none", border: "none", cursor: "pointer",
                    color: "#ccc", fontSize: "14px", padding: "0 2px", lineHeight: "1",
                    display: "inline-flex", alignItems: "center",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = colors.red}
                  onMouseLeave={(e) => e.currentTarget.style.color = "#ccc"}>
                  ×
                </button>
              )}
            </div>
          );
        })}

        {site.has_whole_building_register && site.hide_unsectioned_whole && !wholeRetired && (
          <div onClick={() => toggleUnsectionedHidden("whole", false)}
            style={{ padding: "10px 16px", cursor: "pointer", fontSize: "13px", color: "#94a3b8", fontWeight: "600" }}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.green}
            onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}>
            + Show Whole Building
          </div>
        )}
        {site.has_units && site.hide_unsectioned_units && !unitsRetired && (
          <div onClick={() => toggleUnsectionedHidden("unit_group", false)}
            style={{ padding: "10px 16px", cursor: "pointer", fontSize: "13px", color: "#94a3b8", fontWeight: "600" }}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.green}
            onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}>
            + Show Units
          </div>
        )}

        {showAddCompartment ? (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 8px", flexWrap: "wrap" }}>
            <select
              value={newCompartmentType}
              onChange={(e) => { setNewCompartmentType(e.target.value); setAddCompartmentError(""); }}
              style={{ ...inputStyle, fontSize: "13px", padding: "5px 6px", width: "auto" }}>
              {TYPE_ORDER.map((t) => <option key={t} value={t}>{PART_LABELS[t]}</option>)}
            </select>
            <input
              ref={nameInputRef}
              value={newCompartmentName}
              onChange={(e) => setNewCompartmentName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddCompartment();
                if (e.key === "Escape") { setShowAddCompartment(false); setNewCompartmentName(""); setAddCompartmentError(""); }
              }}
              placeholder={newCompartmentType === "whole" ? "Name (optional)..." : "Name..."}
              style={{ ...inputStyle, fontSize: "13px", padding: "5px 10px", width: "160px" }}
            />
            {addCompartmentError && <span style={{ fontSize: "11px", color: colors.red }}>{addCompartmentError}</span>}
            <button onClick={handleAddCompartment} disabled={addingCompartment}
              style={{
                backgroundColor: colors.green, color: "#fff", border: "none",
                borderRadius: "6px", padding: "5px 14px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
              }}>
              Save
            </button>
            <button onClick={() => { setShowAddCompartment(false); setNewCompartmentName(""); setAddCompartmentError(""); }}
              style={{
                background: "none", border: `1px solid ${colors.border}`, borderRadius: "6px",
                padding: "5px 10px", fontSize: "13px", cursor: "pointer", color: "#666",
              }}>
              Cancel
            </button>
          </div>
        ) : showAddTenantCategory ? (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 8px", flexWrap: "wrap" }}>
            <input
              ref={tenantCategoryInputRef}
              value={newTenantCategoryName}
              onChange={(e) => setNewTenantCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddTenantCategory();
                if (e.key === "Escape") { setShowAddTenantCategory(false); setNewTenantCategoryName(""); setAddTenantCategoryError(""); }
              }}
              placeholder="Tenant category name (e.g. Offices)..."
              style={{ ...inputStyle, fontSize: "13px", padding: "5px 10px", width: "220px" }}
            />
            {addTenantCategoryError && <span style={{ fontSize: "11px", color: colors.red }}>{addTenantCategoryError}</span>}
            <button onClick={handleAddTenantCategory} disabled={addingTenantCategory}
              style={{
                backgroundColor: colors.green, color: "#fff", border: "none",
                borderRadius: "6px", padding: "5px 14px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
              }}>
              Save
            </button>
            <button onClick={() => { setShowAddTenantCategory(false); setNewTenantCategoryName(""); setAddTenantCategoryError(""); }}
              style={{
                background: "none", border: `1px solid ${colors.border}`, borderRadius: "6px",
                padding: "5px 10px", fontSize: "13px", cursor: "pointer", color: "#666",
              }}>
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 8px" }}>
            <IconBtn
              onClick={() => setShowAddCompartment(true)}
              title="Add a building part (Common Area / Warehouse / Building / Whole Building Section)"
              icon={<LuFolderPlus size={16} />}
              label="Part"
              hoverBg="#dcfce7" hoverColor={colors.green}
            />
            <IconBtn
              onClick={() => setShowAddTenantCategory(true)}
              title="Add a new tenant category (e.g. Shops, Offices)"
              icon={<LuTags size={16} />}
              label="Category"
              hoverBg="#ede9fe" hoverColor="#7c3aed"
            />
            {activeTab && (
              <IconBtn
                onClick={() => setShowCreateInspection(true)}
                title="Create an inspection for this section"
                icon={<LuClipboardCheck size={16} />}
                label="Inspection"
                hoverBg="#dbeafe" hoverColor={colors.activeBlue}
              />
            )}
            <IconBtn
              onClick={() => setShowBatchInspection(true)}
              title="Create inspections across multiple sections and split them between techs"
              icon={<LuSplit size={16} />}
              label="Split Inspections"
              hoverBg="#dbeafe" hoverColor={colors.activeBlue}
            />
          </div>
        )}
      </div>

      {content}

      {showCreateInspection && (
        <CreateInspectionModal
          siteId={siteId}
          siteArea={activeTab && activeTab.kind === "area" ? activeTab.area : null}
          pinnedType={activeTab && activeTab.kind === "pinned" ? activeTab.areaType : null}
          onClose={() => setShowCreateInspection(false)}
          onCreate={(inspectionId) => {
            setShowCreateInspection(false);
            setCreatedInspectionId(inspectionId);
          }}
        />
      )}

      {showBatchInspection && (
        <BatchCreateInspectionModal
          siteId={siteId}
          tabs={tabs}
          onClose={() => setShowBatchInspection(false)}
          onCreated={(count) => {
            setShowBatchInspection(false);
            window.alert(`${count} inspection${count !== 1 ? "s" : ""} created. Assign them from the Assign Work page.`);
          }}
        />
      )}
    </div>
  );
}
